import { Link, useNavigate } from '@tanstack/react-router';
import { RefreshCw, Upload, Users } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  ProgressBar,
  SearchField,
  SelectMenu,
  SelectMenuItem,
  SkeletonRows,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '../../../components/ui';
import { useFormat, useT } from '../../../i18n';
import { useAutomationCan, usePoolAccounts } from '../hooks';
import type { PoolAccountSummary } from '../testrun-types';
import { AccountChanges } from './AccountChanges';
import { useCheckAllPool } from './api';
import { listBroken, POOL_COLUMNS, useAccountFormat } from './format';
import { PoolImportPanel } from './ImportPanels';
import { Fail, ReadinessIcon, useReadableError } from './shared';

/** The team's pool: every account that logged in, what it fits for, when it was checked. */

const ANY_FIT = 'any';

const PoolRow = ({ account: a }: { account: PoolAccountSummary }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const { money } = useAccountFormat();
  const navigate = useNavigate();
  const broken = listBroken(a);
  const spent = a.spent ?? [];
  const open = () =>
    void navigate({ to: '/automation/accounts/$accountId', params: { accountId: a.id } });

  return (
    <Tr onClick={open}>
      <Td className="min-w-48">
        <div className="flex flex-col gap-1">
          <Link
            to="/automation/accounts/$accountId"
            params={{ accountId: a.id }}
            onClick={(e) => e.stopPropagation()}
            className="font-semibold text-ink hover:text-accent-fg hover:underline"
          >
            {a.label || (
              <span className="font-normal text-ink-muted">{t('automation.accounts.noLabel')}</span>
            )}
          </Link>
          <span className="flex items-center gap-1.5 text-[12px] text-ink-muted">
            <Badge className="bg-accent-soft text-accent-fg">{a.type}</Badge>
            {a.companyName ?? t('common.empty')}
          </span>
          {spent.length > 0 ? (
            <Badge
              className="self-start bg-skip-soft text-skip"
              title={t('automation.accounts.pool.spentHint', {
                list: spent.map((s) => `${s.class} (${s.at.slice(0, 10)})`).join(', '),
              })}
            >
              {t('automation.accounts.pool.spent', {
                classes: spent.map((s) => s.class).join(', '),
              })}
            </Badge>
          ) : null}
          {a.repair?.status === 'running' ? (
            <span className="text-[12px] text-ink-muted">
              {t('automation.accounts.pool.repairing', { what: a.repair.title })}
            </span>
          ) : null}
          {broken ? (
            <Badge
              className="self-start bg-fail-soft text-fail"
              title={t('automation.accounts.pool.listBrokenHint', {
                reason: broken.detail || t('automation.accounts.pool.listBrokenReason'),
              })}
            >
              {t('automation.accounts.pool.listBroken')}
            </Badge>
          ) : null}
        </div>
      </Td>
      <Td className="whitespace-nowrap font-mono text-[12px]">
        <div>{a.phone}</div>
        <div className="text-ink-muted">
          {a.iin}
          {a.bin ? ` · ${t('automation.accounts.bin')} ${a.bin}` : ''}
        </div>
      </Td>
      <Td className="whitespace-nowrap text-right font-mono">
        {money(a.payerBalance)}
        {a.payerBalance != null ? (
          <div className="text-[11px] text-ink-muted">
            {a.payerIsMain
              ? t('automation.accounts.pool.payerMain')
              : t('automation.accounts.pool.noMain')}
          </div>
        ) : null}
      </Td>
      {POOL_COLUMNS.map((key) => {
        const r = a.readiness.find((x) => x.key === key);
        const column = t(`automation.accounts.pool.col.${key}`);
        const status = r?.status ?? 'unknown';
        return (
          <Td key={key} className="px-1 text-center">
            <ReadinessIcon
              status={status}
              className="justify-center"
              label={`${column}: ${t(`automation.accounts.readiness.${status}`)}${
                r ? ` — ${r.detail}` : ` — ${t('automation.accounts.pool.noData')}`
              }`}
            />
          </Td>
        );
      })}
      <Td className="whitespace-nowrap text-[12px]">
        {a.lastError ? (
          <Badge className="bg-fail-soft text-fail" title={a.lastError.message}>
            {t('automation.accounts.pool.errorAt', { when: relative(a.lastError.at) })}
          </Badge>
        ) : (
          <span className="text-ink-muted">
            {a.checkedAt
              ? `${relative(a.checkedAt)}${a.checkedBy ? `, ${a.checkedBy}` : ''}`
              : t('common.empty')}
          </span>
        )}
      </Td>
    </Tr>
  );
};

export const PoolTab = ({ onCheckTab }: { onCheckTab?: (() => void) | undefined }) => {
  const { t } = useT();
  const { relative } = useFormat();
  const can = useAutomationCan();
  const readable = useReadableError();
  const pool = usePoolAccounts();
  const checkAll = useCheckAllPool();
  const [query, setQuery] = useState('');
  const [fit, setFit] = useState<string>(ANY_FIT);
  const [importing, setImporting] = useState(false);

  if (pool.error) {
    return (
      <Card>
        <ErrorState error={readable(pool.error)} onRetry={() => void pool.refetch()} />
      </Card>
    );
  }
  if (!pool.data) return <SkeletonRows rows={5} className="p-0" />;

  const { accounts, checkAll: progress } = pool.data;
  const needle = query.trim().toLowerCase();
  const rows = accounts
    .filter(
      (a) =>
        !needle ||
        [a.label, a.companyName, a.person, a.phone, a.iin, a.bin].some((v) =>
          v?.toLowerCase().includes(needle),
        ),
    )
    .filter((a) => fit === ANY_FIT || a.readiness.find((r) => r.key === fit)?.status === 'ok');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-[1_1_420px] flex-wrap gap-2">
          <SearchField
            value={query}
            onChange={setQuery}
            label={t('automation.accounts.pool.search')}
            placeholder={t('automation.accounts.pool.searchPlaceholder')}
            className="min-w-56"
          />
          <SelectMenu
            value={fit}
            onValueChange={setFit}
            aria-label={t('automation.accounts.pool.fitFilter')}
            className="w-56"
          >
            <SelectMenuItem value={ANY_FIT}>{t('automation.accounts.pool.fitAny')}</SelectMenuItem>
            {POOL_COLUMNS.map((key) => (
              <SelectMenuItem key={key} value={key}>
                {t('automation.accounts.pool.fitFor', {
                  what: t(`automation.accounts.pool.col.${key}`),
                })}
              </SelectMenuItem>
            ))}
          </SelectMenu>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {progress.running ? (
            <div className="flex items-center gap-2">
              <ProgressBar
                value={progress.total ? (progress.done / progress.total) * 100 : 0}
                className="w-28"
                label={t('automation.accounts.pool.checkAll')}
              />
              <span className="text-[13px] text-ink-muted">
                {t('automation.accounts.pool.checking', {
                  done: progress.done,
                  total: progress.total,
                })}
              </span>
            </div>
          ) : progress.finishedAt ? (
            <span className="text-[13px] text-ink-muted">
              {t('automation.accounts.pool.checkedAll', { when: relative(progress.finishedAt) })}
              {progress.failed
                ? `, ${t('automation.accounts.pool.errors', { count: progress.failed })}`
                : ''}
            </span>
          ) : null}
          {can.run ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setImporting((v) => !v)}
                aria-expanded={importing}
              >
                <Upload className="size-3.5" aria-hidden="true" />
                {t('automation.accounts.import.button')}
              </Button>
              <Button
                size="sm"
                onClick={() => checkAll.mutate()}
                disabled={progress.running || !accounts.length}
                loading={checkAll.isPending}
                title={t('automation.accounts.pool.checkAllHint')}
              >
                {checkAll.isPending ? null : (
                  <RefreshCw
                    className={progress.running ? 'size-3.5 animate-spin' : 'size-3.5'}
                    aria-hidden="true"
                  />
                )}
                {t('automation.accounts.pool.checkAll')}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      {checkAll.isError ? (
        <Fail title={t('automation.accounts.pool.checkAllFailed')} error={checkAll.error} />
      ) : null}
      {importing && can.run ? <PoolImportPanel onClose={() => setImporting(false)} /> : null}

      <AccountChanges live={progress.running} />

      <Card>
        {!accounts.length ? (
          <EmptyState
            icon={<Users className="size-6" />}
            title={t('automation.accounts.pool.empty')}
            description={can.run ? t('automation.accounts.pool.emptyHint') : undefined}
            action={
              can.run && onCheckTab ? (
                <Button onClick={onCheckTab}>{t('automation.accounts.tab.check')}</Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('automation.accounts.pool.account')}</Th>
                    <Th>{t('automation.accounts.pool.phoneIin')}</Th>
                    <Th className="text-right" title={t('automation.accounts.pool.payerHint')}>
                      {t('automation.accounts.pool.payer')}
                    </Th>
                    {POOL_COLUMNS.map((key) => (
                      <Th
                        key={key}
                        className="w-16 whitespace-normal px-1 text-center text-[10px] leading-tight"
                      >
                        {t(`automation.accounts.pool.col.${key}`)}
                      </Th>
                    ))}
                    <Th>{t('automation.accounts.pool.checked')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <PoolRow key={a.id} account={a} />
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            {!rows.length ? (
              <p className="px-4 py-4 text-[13px] text-ink-muted">
                {t('automation.accounts.pool.noMatch')}
              </p>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
};
